/**
 * Additional Documents on landlord Application Detail v2.
 *
 * Two shapes (TT-65):
 * - Template documents section ON → config-driven list: every configured item
 *   renders with its Required flag and upload state (View / Uploaded /
 *   Missing / Not provided), plus any extra uploads not in the config.
 * - Section OFF / legacy app (requiredItems null) → uploads only, exactly the
 *   pre-template behavior.
 * Matching mirrors form-gates seedReviewDocuments: by item id, then by
 * case-insensitive name.
 */
import { Download, Eye } from 'lucide-react'
import { formatDocumentSize } from '#app/utils/application-detail-v2/display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import type { DocumentItem } from '#app/utils/application-form/config'
import { cn } from '#app/utils/misc'
import { SectionCard } from '../primitives'

type UploadedDoc = NonNullable<
	NonNullable<ParsedStepData['review']>['additionalDocuments']
>[number]

/** Blob served through the authenticated proxy so the download carries a
 * Content-Disposition (a cross-origin `download` attribute is ignored) and
 * saves under the applicant's own file name. */
export function blobHref(
	blobUrl: string,
	fileName?: string | null,
	asAttachment = false,
) {
	const params = new URLSearchParams({ url: blobUrl })
	if (fileName?.trim()) params.set('filename', fileName.trim())
	if (asAttachment) params.set('disposition', 'attachment')
	return `/resources/view-blob?${params.toString()}`
}

const ACTION_CLASS =
	'flex size-8 items-center justify-center rounded-lg text-[#32302F] transition-colors hover:bg-[#F0F0F0]'

/** View (opens inline) + Download, right-aligned per the document row design. */
function DocumentActions({
	url,
	fileName,
}: {
	url: string
	fileName?: string | null
}) {
	const label = fileName?.trim() || 'document'
	return (
		<div className="flex shrink-0 items-center gap-1">
			<a
				className={ACTION_CLASS}
				href={blobHref(url, fileName)}
				target="_blank"
				rel="noreferrer"
				title="View"
				aria-label={`View ${label}`}
			>
				<Eye className="size-5" />
			</a>
			<a
				className={ACTION_CLASS}
				href={blobHref(url, fileName, true)}
				title="Download"
				aria-label={`Download ${label}`}
			>
				<Download className="size-5" />
			</a>
		</div>
	)
}

/**
 * One document row (client 2026-08-27): the FILE NAME leads in dark medium
 * text with a muted second line under it, replacing the label-over-value
 * FieldBox — which put the file name in the small gray LABEL slot and a bare
 * "Available" where the useful detail belongs.
 *
 * `detail` is the upload's size when one was recorded, else its status text.
 * A missing required item passes tone="danger" so "Missing" stays red exactly
 * as it read before.
 */
function DocumentRow({
	name,
	detail,
	tone = 'muted',
	url,
	fileName,
}: {
	name: string
	detail: string
	tone?: 'muted' | 'danger'
	url?: string | null
	/** The upload's own file name — used for the saved download name. */
	fileName?: string | null
}) {
	return (
		<div className="flex items-center justify-between gap-4 rounded-2xl border border-[#F0F0F0] bg-[#fbfdfd] px-5 py-4">
			<div className="min-w-0">
				<p className="truncate text-[16px] font-medium text-[#32302F]">
					{name}
				</p>
				<p
					className={cn(
						'mt-1 text-[15px]',
						tone === 'danger' ? 'text-[#DC2626]' : 'text-[#747474]',
					)}
				>
					{detail}
				</p>
			</div>
			{url ? <DocumentActions url={url} fileName={fileName ?? name} /> : null}
		</div>
	)
}

function UploadRow({ doc }: { doc: UploadedDoc }) {
	const name = doc.fileName ?? doc.name ?? 'Document'
	const url = doc.blobUrl ?? null
	return (
		<DocumentRow
			name={name}
			// Size when the upload recorded one, else the old availability wording.
			detail={formatDocumentSize(doc.fileSize) ?? (url ? 'Available' : '-')}
			url={url}
		/>
	)
}

/**
 * The document rows alone — the individual card's body and the combined
 * report's per-member accordion body (2026-09-08) render the SAME rows.
 */
export function AdditionalDocumentsBody({
	documents,
	requiredItems,
	className,
}: {
	documents: NonNullable<ParsedStepData['review']>['additionalDocuments']
	requiredItems?: DocumentItem[] | null
	className?: string
}) {
	const docs = Array.isArray(documents) ? documents : []
	const items = requiredItems ?? null

	if (!items || items.length === 0) {
		return (
			<div className={cn('space-y-4', className)}>
				{docs.map((d) => (
					<UploadRow key={d.id} doc={d} />
				))}
			</div>
		)
	}

	const byId = new Map(docs.map((doc) => [doc.id, doc]))
	const byName = new Map(
		docs.map((doc) => [doc.name.trim().toLowerCase(), doc]),
	)
	const matchedIds = new Set<string>()
	const configuredRows = items.map((item) => {
		const match =
			byId.get(item.id) ?? byName.get(item.name.trim().toLowerCase()) ?? null
		if (match) matchedIds.add(match.id)
		const uploaded = Boolean(match && (match.documentId || match.fileName))
		return { item, match, uploaded }
	})
	const extraUploads = docs.filter((doc) => !matchedIds.has(doc.id))

	return (
		<div className={cn('space-y-4', className)}>
			{configuredRows.map(({ item, match, uploaded }) => {
				const url = match?.blobUrl ?? null
				const label = item.mandatory ? `${item.name} (Required)` : item.name
				// Uploaded → the FILE's name leads, with the configured item name
				// kept on the detail line so the row still says what it satisfies.
				// Not uploaded → the item name leads and the detail line carries the
				// unchanged Missing / Not provided wording and colour.
				return uploaded ? (
					<DocumentRow
						key={item.id}
						name={match?.fileName ?? 'Uploaded'}
						detail={formatDocumentSize(match?.fileSize) ?? label}
						url={url}
					/>
				) : (
					<DocumentRow
						key={item.id}
						name={label}
						detail={item.mandatory ? 'Missing' : 'Not provided'}
						tone={item.mandatory ? 'danger' : 'muted'}
					/>
				)
			})}
			{extraUploads.map((doc) => (
				<UploadRow key={doc.id} doc={doc} />
			))}
		</div>
	)
}

export function AdditionalDocuments({
	documents,
	requiredItems,
}: {
	documents: NonNullable<ParsedStepData['review']>['additionalDocuments']
	/** The template's configured document list (TT-65); null/absent when the
	 * documents section is off — renders the legacy uploads-only list. */
	requiredItems?: DocumentItem[] | null
}) {
	const docs = Array.isArray(documents) ? documents : []
	const items = requiredItems ?? null
	if ((!items || items.length === 0) && docs.length === 0) return null

	return (
		<section className={cn('p-0')}>
			<SectionCard
				iconSrc="/img/additional-document-icon.svg"
				title="Additional Documents"
				columns={1}
			>
				<AdditionalDocumentsBody documents={docs} requiredItems={items} />
			</SectionCard>
		</section>
	)
}
